import { Badge, Button } from "@xpense/ui";
import { Transaction } from "../api/client";
import { Icon } from "./Icons";
import TransactionNutritionBadge from "./TransactionNutritionBadge";

interface TransactionCardProps {
  transaction: Transaction;
  onEdit?: (transaction: Transaction) => void;
  onDelete?: (transaction: Transaction) => void;
}

const statusToBadgeVariant: Record<string, "success" | "warning" | "error" | "info"> = {
  classified: "success",
  confirmed: "success",
  needs_review: "warning",
  pending: "info",
  failed: "error",
};

const typeLabels: Record<string, string> = {
  expense: "Expense",
  income: "Income",
  transfer: "Transfer",
  investment: "Investment",
  loan: "Loan",
  reimbursement: "Reimbursement",
};

const taxStatusToBadgeVariant: Record<string, "success" | "warning" | "error" | "info"> = {
  calculated: "success",
  needs_review: "warning",
  unavailable: "error",
  not_applicable: "info",
};

export default function TransactionCard({ transaction, onEdit, onDelete }: TransactionCardProps) {
  const formatINR = (amount: number) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);

  const isIncome = transaction.txn_type === "income";
  const normalizedAmount = Math.abs(transaction.amount) < 0.005 ? 0 : Math.abs(transaction.amount);
  const amountPrefix = normalizedAmount === 0 ? "" : isIncome ? "+" : "-";
  const amountColor = isIncome ? "var(--xpn-color-success)" : "var(--xpn-color-danger)";
  const merchant = transaction.merchant || "Unresolved merchant";
  const initial = merchant.trim().slice(0, 1).toUpperCase() || "X";
  const confidence =
    transaction.confidence !== null && transaction.confidence !== undefined
      ? Math.round(transaction.confidence * 100)
      : null;
  const taxSummary = transaction.tax_summary;
  const taxItems = transaction.tax_items ?? [];
  const taxStatus = taxSummary?.status;
  const nutritionItems = transaction.nutrition_items ?? [];

  return (
    <article className="xpn-txn-row">
      <div className="xpn-txn-main">
        <div className="xpn-txn-icon">{initial}</div>
        <div style={{ minWidth: 0 }}>
          <div className="xpn-txn-title">{merchant}</div>
          <div className="xpn-txn-meta">
            {new Date(transaction.timestamp).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
            {" · "}
            {typeLabels[transaction.txn_type] || transaction.txn_type}
          </div>

          <div className="xpn-txn-tags">
            {transaction.category && <Badge variant="soft">{transaction.category}</Badge>}
            {transaction.payment_mode && <Badge variant="neutral">{transaction.payment_mode}</Badge>}
            {transaction.source && <Badge variant="neutral">{transaction.source}</Badge>}
            <Badge variant={statusToBadgeVariant[transaction.parse_status] || "info"}>
              {transaction.parse_status.replace("_", " ")}
            </Badge>
            {taxStatus && taxStatus !== "not_applicable" && (
              <Badge variant={taxStatusToBadgeVariant[taxStatus] || "info"}>
                tax {taxStatus.replace("_", " ")}
              </Badge>
            )}
          </div>

          {confidence !== null && (
            <div style={{ marginTop: "var(--xpn-space-md)", maxWidth: 280 }}>
              <div className="xpn-flex-between xpn-mb-xs">
                <span className="xpn-text-caption xpn-text-muted">AI confidence</span>
                <span data-tnum className="xpn-text-caption-tabular xpn-text-muted">
                  {confidence}%
                </span>
              </div>
              <div className="xpn-progress">
                <div
                  className="xpn-progress-bar"
                  style={{
                    width: `${Math.max(2, Math.min(100, confidence))}%`,
                    background:
                      confidence >= 70
                        ? "var(--xpn-color-success)"
                        : confidence >= 50
                          ? "var(--xpn-color-warning)"
                          : "var(--xpn-color-danger)",
                  }}
                />
              </div>
            </div>
          )}

          {nutritionItems.length > 0 && (
            <div style={{ marginTop: "var(--xpn-space-md)" }}>
              <TransactionNutritionBadge items={nutritionItems} />
            </div>
          )}

          {transaction.raw_text && (
            <details style={{ marginTop: "var(--xpn-space-md)" }}>
              <summary className="xpn-text-caption xpn-text-muted" style={{ cursor: "pointer" }}>
                Raw event
              </summary>
              <div
                className="xpn-input"
                style={{
                  height: "auto",
                  minHeight: 0,
                  marginTop: "var(--xpn-space-sm)",
                  padding: "var(--xpn-space-md)",
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {transaction.raw_text}
              </div>
            </details>
          )}

          {taxItems.length > 0 && (
            <details style={{ marginTop: "var(--xpn-space-md)" }}>
              <summary className="xpn-text-caption xpn-text-muted" style={{ cursor: "pointer" }}>
                Tax details
              </summary>
              <div
                style={{
                  display: "grid",
                  gap: "var(--xpn-space-sm)",
                  marginTop: "var(--xpn-space-sm)",
                }}
              >
                {taxItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid var(--xpn-border)",
                      borderRadius: "var(--xpn-radius-md)",
                      padding: "var(--xpn-space-md)",
                    }}
                  >
                    <div className="xpn-flex-between" style={{ gap: "var(--xpn-space-md)" }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                          {item.item_label}
                        </div>
                        <div className="xpn-text-caption xpn-text-muted">
                          {item.tax_basis} · {item.calculation_source.replace("_", " ")}
                          {item.gst_rate !== null ? ` · GST ${item.gst_rate}%` : ""}
                          {item.cess_rate !== null ? ` · Cess ${item.cess_rate}%` : ""}
                        </div>
                      </div>
                      <div data-tnum className="xpn-text-body-tabular xpn-text">
                        {formatINR(item.total_tax_amount)}
                      </div>
                    </div>

                    {item.tax_components.length > 0 && (
                      <div className="xpn-txn-tags" style={{ marginTop: "var(--xpn-space-sm)" }}>
                        {item.tax_components.map((component, index) => (
                          <Badge key={`${item.id}-${component.type}-${index}`} variant="neutral">
                            {component.label}: {formatINR(component.amount)}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {item.review_reason && (
                      <div className="xpn-text-caption xpn-text-muted xpn-mt-sm">
                        {item.review_reason}
                      </div>
                    )}

                    {item.rate_source_url && (
                      <a
                        className="xpn-text-caption"
                        href={item.rate_source_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: "inline-block", marginTop: "var(--xpn-space-sm)" }}
                      >
                        {item.rate_source_title || "Tax source"}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      <div className="xpn-txn-amount">
        <div data-tnum className="xpn-text-heading-sm" style={{ color: amountColor }}>
          {amountPrefix}
          {formatINR(normalizedAmount)}
        </div>
        {transaction.account_last4 && (
          <div className="xpn-text-caption xpn-text-muted">Acct {transaction.account_last4}</div>
        )}
        {taxSummary && taxSummary.status !== "not_applicable" && (
          <div className="xpn-text-caption xpn-text-muted">
            Tax {formatINR(taxSummary.total_tax_paid)}
          </div>
        )}
      </div>

      {(onEdit || onDelete) && (
        <div className="xpn-txn-actions">
          {onEdit && (
            <Button variant="secondary" size="sm" onClick={() => onEdit(transaction)}>
              Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="danger" size="sm" onClick={() => onDelete(transaction)}>
              <Icon name="trash" />
              Delete
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
